import React, { useState } from 'react';
import { CheckCircle2Icon, ChevronDownIcon, ChevronUpIcon, CircleIcon, EyeIcon, EyeOffIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { FormConfig, SectionConfig } from '../../types';
import { allFieldIds, createSectionId, fieldCatalog, moveItem } from '../../utils/config';
import { SECTION_ICON_NAMES, resolveIcon } from '../../utils/icons';
import { AddFieldPicker } from './AddFieldPicker';
import { Segmented, Select } from './AdminControls';
import { MoveFieldMenu } from './MoveFieldMenu';
interface SectionsTabProps {
  config: FormConfig;
  onChange: (config: FormConfig) => void;
}
export function SectionsTab({
  config,
  onChange
}: SectionsTabProps) {
  const [expanded, setExpanded] = useState<string | null>(config.sections[0]?.id ?? null);
  const [addingIn, setAddingIn] = useState<string | null>(null);
  const updateSection = (id: string, patch: Partial<SectionConfig>) => {
    onChange({
      ...config,
      sections: config.sections.map(s => s.id === id ? {
        ...s,
        ...patch
      } : s)
    });
  };
  const moveSection = (index: number, direction: -1 | 1) => {
    onChange({
      ...config,
      sections: moveItem(config.sections, index, direction)
    });
  };
  const addSection = () => {
    const id = createSectionId();
    onChange({
      ...config,
      sections: [...config.sections, {
        id,
        title: 'New Section',
        icon: 'ClipboardList',
        hidden: false,
        columns: 2,
        defaultCollapsed: false,
        fieldIds: []
      }]
    });
    setExpanded(id);
  };
  const deleteSection = (id: string) => {
    const removed = config.sections.find(s => s.id === id);
    const remaining = config.sections.filter(s => s.id !== id);
    if (!removed || remaining.length === 0) return;
    // Orphaned fields fall back into the first remaining section.
    remaining[0] = {
      ...remaining[0],
      fieldIds: [...remaining[0].fieldIds, ...removed.fieldIds]
    };
    onChange({
      ...config,
      sections: remaining
    });
  };
  const toggleFieldVisibility = (fieldId: string) => {
    const hidden = config.hiddenFieldIds.includes(fieldId) ? config.hiddenFieldIds.filter(f => f !== fieldId) : [...config.hiddenFieldIds, fieldId];
    onChange({
      ...config,
      hiddenFieldIds: hidden
    });
  };
  const toggleFieldRequired = (fieldId: string) => {
    const required = config.requiredFieldIds.includes(fieldId) ? config.requiredFieldIds.filter(f => f !== fieldId) : [...config.requiredFieldIds, fieldId];
    onChange({
      ...config,
      requiredFieldIds: required
    });
  };
  const setSectionRequired = (sectionId: string, required: boolean) => {
    const section = config.sections.find(s => s.id === sectionId);
    if (!section) return;
    const others = config.requiredFieldIds.filter(id => !section.fieldIds.includes(id));
    onChange({
      ...config,
      requiredFieldIds: required ? [...others, ...section.fieldIds] : others
    });
  };
  const moveField = (sectionId: string, index: number, direction: -1 | 1) => {
    const section = config.sections.find(s => s.id === sectionId);
    if (!section) return;
    updateSection(sectionId, {
      fieldIds: moveItem(section.fieldIds, index, direction)
    });
  };
  const placedFieldIds = new Set(config.sections.flatMap(s => s.fieldIds));
  const availableFields = allFieldIds.filter(id => !placedFieldIds.has(id)).map(id => fieldCatalog[id]).filter(Boolean);
  const addFieldToSection = (sectionId: string, fieldId: string) => {
    onChange({
      ...config,
      sections: config.sections.map(s => s.id === sectionId ? {
        ...s,
        fieldIds: [...s.fieldIds, fieldId]
      } : s)
    });
    setAddingIn(null);
  };

  /** Take a field off the layout entirely — it returns to the available list. */
  const removeFieldFromLayout = (fieldId: string) => {
    onChange({
      ...config,
      sections: config.sections.map(s => ({
        ...s,
        fieldIds: s.fieldIds.filter(id => id !== fieldId)
      })),
      hiddenFieldIds: config.hiddenFieldIds.filter(id => id !== fieldId),
      requiredFieldIds: config.requiredFieldIds.filter(id => id !== fieldId)
    });
  };
  const moveFieldToSection = (fromId: string, fieldId: string, toId: string) => {
    if (fromId === toId) return;
    onChange({
      ...config,
      sections: config.sections.map(s => {
        if (s.id === fromId) return {
          ...s,
          fieldIds: s.fieldIds.filter(f => f !== fieldId)
        };
        if (s.id === toId) return {
          ...s,
          fieldIds: [...s.fieldIds, fieldId]
        };
        return s;
      })
    });
  };
  return <div className="space-y-3" data-id="element-135" data-mp="1xlhmosfd76ha">
      <p className="text-xs leading-relaxed text-slate-500" data-id="element-136" data-mp="21o4hn27c17ip">
        Reorder sections, rename them, move fields between them, control which fields appear on the
        record, and choose which ones count toward record completion.
      </p>

      {config.sections.map((section, index) => {
      const Icon = resolveIcon(section.icon);
      const isOpen = expanded === section.id;
      const hiddenInSection = section.fieldIds.filter(f => config.hiddenFieldIds.includes(f)).length;
      const requiredInSection = section.fieldIds.filter(f => config.requiredFieldIds.includes(f)).length;
      return <div key={section.id} className={`overflow-hidden rounded-xl border bg-white ${section.hidden ? 'border-slate-200 opacity-60' : 'border-slate-200'}`} data-id="element-137" data-mp="1e3vclf86qbvw" data-mpi={index}>
            <div className="flex items-center gap-2 px-3 py-2.5" data-id="element-138" data-mp="21rmpol1jusy8">
              <div className="flex flex-col" data-id="element-139" data-mp="21sxw6smdqet1">
                <button type="button" aria-label={`Move ${section.title} up`} disabled={index === 0} onClick={() => moveSection(index, -1)} className="text-slate-400 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-140" data-mp="1wt1qmoy1afbd">
                  <ChevronUpIcon className="h-3.5 w-3.5" data-id="element-141" data-mp="2dv7jn1tir19d" />
                </button>
                <button type="button" aria-label={`Move ${section.title} down`} disabled={index === config.sections.length - 1} onClick={() => moveSection(index, 1)} className="text-slate-400 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-142" data-mp="2iszb5uq94ld1">
                  <ChevronDownIcon className="h-3.5 w-3.5" data-id="element-143" data-mp="31ifoxjqy6abe" />
                </button>
              </div>

              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600" data-id="element-144" data-mp="36him6i0orc8t">
                <Icon className="h-4 w-4" data-id="element-145" data-mp="3bg3hq44zt7qx" />
              </span>

              <input value={section.title} aria-label="Section title" onChange={e => updateSection(section.id, {
            title: e.target.value
          })} className="min-w-0 flex-1 rounded-md border border-transparent px-1.5 py-1 text-sm font-semibold text-slate-800 outline-none transition-colors hover:border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-146" data-mp="1c4ali9q146f9" />

              <button type="button" aria-label={section.hidden ? 'Show section' : 'Hide section'} onClick={() => updateSection(section.id, {
            hidden: !section.hidden
          })} className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-id="element-147" data-mp="3llv06u06lqk9">
                {section.hidden ? <EyeOffIcon className="h-4 w-4" data-id="element-148" data-mp="889tbpkut114" /> : <EyeIcon className="h-4 w-4" data-id="element-149" data-mp="36khw8tpylci" />}
              </button>

              <button type="button" aria-label="Configure section" aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : section.id)} className="flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50" data-id="element-150" data-mp="2sx7c6qo1sn30">
                {section.fieldIds.length}
                <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-151" data-mp="hxjx2qbnhucz" />
              </button>
            </div>

            {isOpen && <div className="space-y-3 border-t border-slate-100 bg-slate-50 p-3" data-id="element-152" data-mp="1alg16g3juojs">
                <div className="flex flex-wrap items-end gap-3" data-id="element-153" data-mp="1fj7v1fnr8kqm">
                  <div data-id="element-154" data-mp="10vmspnypepjr">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-155" data-mp="10u3cfxd1sehz">
                      Columns
                    </span>
                    <Segmented ariaLabel="Section columns" value={String(section.columns)} options={[{
                value: '1',
                label: '1 col'
              }, {
                value: '2',
                label: '2 col'
              }]} onChange={v => updateSection(section.id, {
                columns: Number(v) === 1 ? 1 : 2
              })} data-id="element-156" data-mp="1tj0kz8rgiwxj" />
                  </div>
                  <div data-id="element-157" data-mp="3nr4anny8mbwm">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-158" data-mp="3nsp4tcgn3sty">
                      Default state
                    </span>
                    <Segmented ariaLabel="Default state" value={section.defaultCollapsed ? 'collapsed' : 'expanded'} options={[{
                value: 'expanded',
                label: 'Expanded'
              }, {
                value: 'collapsed',
                label: 'Collapsed'
              }]} onChange={v => updateSection(section.id, {
                defaultCollapsed: v === 'collapsed'
              })} data-id="element-159" data-mp="2v3iayqs4rrf1" />
                  </div>
                  <Select label="Icon" className="w-32" value={section.icon} onChange={v => updateSection(section.id, {
              icon: v
            })} options={SECTION_ICON_NAMES.map(n => ({
              value: n,
              label: n
            }))} data-id="element-160" data-mp="3i84g7jtphelm" />
                  {config.sections.length > 1 && <button type="button" onClick={() => deleteSection(section.id)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50" data-id="element-161" data-mp="2zh3h8vsl5j22">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-162" data-mp="onh27hz9hjt5" />
                      Delete
                    </button>}
                </div>

                <div className="rounded-lg border border-slate-200 bg-white" data-id="element-163" data-mp="joxhfkweme2d">
                  <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2" data-id="element-164" data-mp="r6vntgjah91d">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-165" data-mp="w5p23b6eh42l">
                      Fields
                    </span>
                    {hiddenInSection > 0 && <span className="text-[11px] font-medium text-slate-400" data-id="element-166" data-mp="15w9x6omv7qgr">
                        {hiddenInSection} hidden
                      </span>}
                    <span className="text-[11px] font-medium text-blue-600" data-id="element-167" data-mp="d971e49kptu7">
                      {requiredInSection} required
                    </span>
                    <span className="ml-auto flex items-center gap-1" data-id="element-168" data-mp="i9lalpmwv9tt">
                      <button type="button" onClick={() => setSectionRequired(section.id, true)} className="rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-blue-600" data-id="element-169" data-mp="madhwdc834ng">
                        Require all
                      </button>
                      <button type="button" onClick={() => setSectionRequired(section.id, false)} className="rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700" data-id="element-170" data-mp="3prmr2f67gfb7">
                        None
                      </button>
                    </span>
                  </div>
                  <p className="border-b border-slate-100 px-3 py-1.5 text-[11px] text-slate-400" data-id="element-171" data-mp="3krh6fiycrt2m">
                    Arrows reorder, <span className="font-semibold" data-id="element-172" data-mp="3fy5z7gpmpaa6">Move</span> sends a field to
                    another section, the circle marks completion tracking, and the eye controls
                    visibility.
                  </p>
                  <ul className="max-h-72 overflow-y-auto" data-id="element-173" data-mp="2n97dyaq7ls0n">
                    {section.fieldIds.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400" data-id="element-174" data-mp="17jhbqj2iukl2">
                        No fields yet — move fields here from another section.
                      </li>}
                    {section.fieldIds.map((fieldId, fieldIndex) => {
                const field = fieldCatalog[fieldId];
                if (!field) return null;
                const isHidden = config.hiddenFieldIds.includes(fieldId);
                const isRequired = config.requiredFieldIds.includes(fieldId);
                return <li key={fieldId} className="group flex items-center gap-1.5 border-b border-slate-50 px-2 py-1.5 last:border-0" data-id="element-175" data-mp="1cl6rgc0ovmfh" data-mpi={fieldIndex}>
                          <div className="flex flex-col" data-id="element-176" data-mp="62gvhubsye8n">
                            <button type="button" aria-label={`Move ${field.label} up`} disabled={fieldIndex === 0} onClick={() => moveField(section.id, fieldIndex, -1)} className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-177" data-mp="641qfl213z71">
                              <ChevronUpIcon className="h-3 w-3" data-id="element-178" data-mp="14azk0mu4p15u" />
                            </button>
                            <button type="button" aria-label={`Move ${field.label} down`} disabled={fieldIndex === section.fieldIds.length - 1} onClick={() => moveField(section.id, fieldIndex, 1)} className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25" data-id="element-179" data-mp="109xk03se0l2t">
                              <ChevronDownIcon className="h-3 w-3" data-id="element-180" data-mp="3b3s8eziml7i6" />
                            </button>
                          </div>

                          <span className={`min-w-0 flex-1 truncate text-xs ${isHidden ? 'text-slate-400 line-through' : 'text-slate-700'}`} data-id="element-181" data-mp="35wlom4n1j0b6">
                            {field.label}
                          </span>


                          <button type="button" aria-pressed={isRequired} aria-label={`${isRequired ? 'Remove' : 'Add'} ${field.label} ${isRequired ? 'from' : 'to'} record completion`} onClick={() => toggleFieldRequired(fieldId)} className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors ${isRequired ? 'text-blue-600 hover:bg-blue-50' : 'text-slate-300 hover:bg-slate-100 hover:text-slate-500'}`} data-id="element-182" data-mp="3jngquy31aj5s">
                            {isRequired ? <CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-183" data-mp="262wx9y8h7ohh" /> : <CircleIcon className="h-3.5 w-3.5" data-id="element-184" data-mp="2189hcnrjg2ei" />}
                          </button>

                          {config.sections.length > 1 && <MoveFieldMenu fieldLabel={field.label} currentSectionTitle={section.title} targets={config.sections.filter(s => s.id !== section.id).map(s => ({
                    id: s.id,
                    title: s.title
                  }))} onMove={toId => moveFieldToSection(section.id, fieldId, toId)} data-id="element-185" data-mp="2a4ig007oywzl" />}

                          <button type="button" aria-label={isHidden ? `Show ${field.label}` : `Hide ${field.label}`} onClick={() => toggleFieldVisibility(fieldId)} className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${isHidden ? 'text-slate-400 hover:bg-slate-100' : 'text-blue-500 hover:bg-blue-50'}`} data-id="element-186" data-mp="3hhz4aegoiwja">
                            {isHidden ? <EyeOffIcon className="h-3.5 w-3.5" data-id="element-187" data-mp="2oubmf76smk0c" /> : <EyeIcon className="h-3.5 w-3.5" data-id="element-188" data-mp="2autwuplob2px" />}
                          </button>

                          <button type="button" aria-label={`Remove ${field.label} from the layout`} title="Remove from layout" onClick={() => removeFieldFromLayout(fieldId)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-189" data-mp="2fuom6opsvk8v">
                            <Trash2Icon className="h-3.5 w-3.5" data-id="element-190" data-mp="2cdc3m321s9fb" />
                          </button>
                        </li>;
              })}
                  </ul>

                  <div className="border-t border-slate-100 p-2" data-id="element-191" data-mp="22mhbii24efly">
                    {addingIn === section.id ? <AddFieldPicker sectionTitle={section.title} available={availableFields} onCancel={() => setAddingIn(null)} onAdd={fieldId => addFieldToSection(section.id, fieldId)} data-id="element-192" data-mp="1yi9nkn3hl3os" /> : <button type="button" onClick={() => setAddingIn(section.id)} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600" data-id="element-193" data-mp="92phpmlzlg2y">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-194" data-mp="3mhvj4gnmpojy" />
                        Add field to this section
                        <span className="font-normal text-slate-400" data-id="element-195" data-mp="3qj705xsft1gj">
                          ({availableFields.length} available)
                        </span>
                      </button>}
                  </div>
                </div>
              </div>}
          </div>;
    })}

      <button type="button" onClick={addSection} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600" data-id="element-196" data-mp="32stj12be4n0q">
        <PlusIcon className="h-4 w-4" data-id="element-197" data-mp="2jafsgw655isa" />
        Add section
      </button>
    </div>;
}